import { bindActionCreators } from "@reduxjs/toolkit";
import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import {
  isUserDeleted,
  formatUserName,
  getLinkNavigationProps,
  type PerformanceWithAccountFilter,
  canUseChildAccount,
  isUserFromCurrentAccount as isUserFromCurrentAccountUtil,
} from "features/Library/Common/utils/performanceUtils";
import { GenericPerformanceList } from "features/Reporting/Content/shared/GenericPerformanceList";
import { type FC, useEffect, useMemo } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { selectPeopleDetails, selectPeopleDetailsValue } from "../state/slices/surveyPerformanceSlice";
import * as surveyPerformanceActions from "../state/thunks/surveyPerformanceThunk";
import { type SurveyPeopleDetails } from "../types/state";
import { bindAction } from "interfaces";
import { setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { RStatus } from "features/Application/globaltypes/fetchRequest";
import { RouteNames } from "enums";

import styles from "./surveyPerformancePeople.module.scss";
import { type ColumnOption } from "interfaces/columnOptions";
import { MoboLink } from "components/MoboLink/MoboLink";
import { useReportingFormatWithTimeZone } from "hooks/useReportingFormatWithTimeZone";

export type SurveyPerformancePeopleProps = {
  surveyId: number;
  flowId?: number;
  filter: PerformanceWithAccountFilter;
  includeAccounts?: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
} & PropsFromRedux;

export const SurveyPerformancePeopleComponent: FC<SurveyPerformancePeopleProps> = ({
  actions,
  surveyId,
  flowId,
  filter,
  peopleDetails,
  peopleDetailsStatus,
  setHasExportData,
  accountId,
  includeAccounts,
  childAccountId,
  hasCreateAccountsAddOn,
}) => {
  const formatDateTime = useReportingFormatWithTimeZone();
  const getColumnOptions = useMemo(
    () => (accountId: number) => {
      const baseColumnOptions: ColumnOption<SurveyPeopleDetails>[] = [
        {
          name: "NAME",
          width: 5,
          isSortable: false,
          render: (entity: SurveyPeopleDetails) => {
            const formattedNameDisplay = formatUserName(entity.FirstName, entity.LastName);
            const childAccountEnabled = canUseChildAccount(childAccountId, hasCreateAccountsAddOn);
            const shouldShowLink =
              !isUserDeleted(entity) && (!includeAccounts || childAccountEnabled || !childAccountId);
            const isUserFromCurrentAccount = isUserFromCurrentAccountUtil(
              entity.AccountId,
              accountId,
              childAccountId,
              hasCreateAccountsAddOn,
            );
            const { accountId: linkAccountId, target: linkTarget } = getLinkNavigationProps({
              childAccountEnabled,
              isUserFromCurrentAccount,
              childAccountId,
              fallbackAccountId: entity.AccountId,
            });

            const userName = (
              <Tooltip target={<TextTruncate>{formattedNameDisplay}</TextTruncate>} content={formattedNameDisplay} />
            );
            const secondaryInfo = [entity.UserTitle, entity.Department].filter((x) => x).join(", ");
            return (
              <div>
                {shouldShowLink ? (
                  <MoboLink
                    className={styles.linkButton}
                    target={linkTarget}
                    to={`/${RouteNames.peopleUsers}/${entity.UserId}`}
                    accountId={linkAccountId}
                  >
                    {userName}
                  </MoboLink>
                ) : (
                  userName
                )}
                <Tooltip
                  content={secondaryInfo}
                  target={<TextTruncate className="workTitle">{secondaryInfo}</TextTruncate>}
                />
              </div>
            );
          },
        },
        {
          name: "EMAIL",
          isSortable: false,
          width: 5,
          render: (entity: SurveyPeopleDetails) => (
            <div>
              <Tooltip target={<TextTruncate>{entity.Email}</TextTruncate>} content={entity.Email} />
              <div>&nbsp;</div>
            </div>
          ),
        },
        {
          name: "QUESTIONS COMPLETED",
          width: 6,
          isSortable: false,
          render: (entity: SurveyPeopleDetails) => (
            <div>
              <div>{entity.QuestionsCompleted}</div>
              <div>&nbsp;</div>
            </div>
          ),
        },
        {
          name: "TIMES TAKEN",
          width: 4,
          isSortable: false,
          render: (entity: SurveyPeopleDetails) => (
            <div>
              <div>{entity.TimesTaken}</div>
              <div>&nbsp;</div>
            </div>
          ),
        },
        {
          name: "STARTED",
          width: 4,
          isSortable: false,
          render: (entity: SurveyPeopleDetails) => {
            const date = formatDateTime(entity.StartDate);

            return (
              <div>
                <div>{date}</div>
                <div>&nbsp;</div>
              </div>
            );
          },
        },
        {
          name: "COMPLETED",
          width: 4,
          isSortable: false,
          render: (entity: SurveyPeopleDetails) => {
            const date = formatDateTime(entity.CompletionDate);

            return (
              <div>
                <div>{date}</div>
                <div>&nbsp;</div>
              </div>
            );
          },
        },
      ];

      return baseColumnOptions;
    },
    [formatDateTime, includeAccounts, childAccountId, hasCreateAccountsAddOn],
  );
  const columnOptions = getColumnOptions(accountId);
  useEffect(() => {
    if (filter.type !== undefined) {
      actions.fetchPeopleDetails(surveyId, { ...filter, flowId });
    }
  }, [surveyId, actions, filter, flowId]);

  useEffect(() => {
    setHasExportData({
      hasDataToExport: peopleDetailsStatus.status === RStatus.Got && !!peopleDetails.length,
      tooltipContent: "No data available for export. This survey contains no data.",
    });
  }, [setHasExportData, peopleDetailsStatus.status, peopleDetails]);

  return (
    <RequestStatusRenderer state={peopleDetailsStatus}>
      <GenericPerformanceList rows={peopleDetails} columns={columnOptions} filter={filter} title="People" />
    </RequestStatusRenderer>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    peopleDetails: selectPeopleDetailsValue(state),
    peopleDetailsStatus: selectPeopleDetails(state),
    accountId: state.userProfile.accountId,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    actions: bindActionCreators(surveyPerformanceActions, dispatch),
    setHasExportData: bindAction(setHasDataToExport, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(SurveyPerformancePeopleComponent);
export default ConnectedComponent;
